<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LiaAgentsAPI from 'dashboard/api/liaAgents';

const props = defineProps({
  agent: {
    type: Object,
    default: null,
  },
  modelValue: {
    type: Object,
    default: () => ({
      enabled: false,
      phone_numbers: [],
    }),
  },
});

const emit = defineEmits(['update:modelValue', 'save']);

const { t } = useI18n();

const testModeConfig = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
});

const newPhoneNumber = ref('');
const isSavingTestMode = ref(false);

// Toggle enabled
const toggleEnabled = () => {
  emit('update:modelValue', {
    ...props.modelValue,
    enabled: !props.modelValue.enabled,
  });
};

// Add phone number to test list
const addTestPhoneNumber = () => {
  const phone = newPhoneNumber.value.trim();
  if (!phone) return;

  // Remove all non-digits except leading +
  let formatted = phone.startsWith('+') ? '+' : '';
  formatted += phone.replace(/\D/g, '');

  // Validate length (minimum 10 digits with country code)
  const digitsOnly = formatted.replace(/\D/g, '');
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    useAlert(t('LIA_AGENTS.TEST_MODE.INVALID_PHONE'));
    return;
  }

  // Ensure it starts with +
  if (!formatted.startsWith('+')) {
    formatted = '+' + formatted;
  }

  if (props.modelValue.phone_numbers.includes(formatted)) {
    useAlert(t('LIA_AGENTS.TEST_MODE.DUPLICATE_PHONE'));
    return;
  }

  emit('update:modelValue', {
    ...props.modelValue,
    phone_numbers: [...props.modelValue.phone_numbers, formatted],
  });
  newPhoneNumber.value = '';
};

// Remove phone number from test list
const removeTestPhoneNumber = phone => {
  const filtered = props.modelValue.phone_numbers.filter(p => p !== phone);
  emit('update:modelValue', {
    ...props.modelValue,
    phone_numbers: filtered,
  });
};

// Save test mode settings
const saveTestModeSettings = async () => {
  isSavingTestMode.value = true;
  try {
    emit('save');

    // Update test mode in n8n workflow
    const hasWorkflow =
      props.agent?.n8n_workflow_id ||
      props.agent?.metadata?.workflow_data?.main_workflow_id;

    if (hasWorkflow) {
      useAlert(
        props.modelValue.enabled
          ? t('LIA_AGENTS.TEST_MODE.REDEPLOY_TITLE')
          : t('LIA_AGENTS.TEST_MODE.REDEPLOY_TITLE')
      );

      try {
        await LiaAgentsAPI.updateTestMode(props.agent.id);
        const statusMsg = props.modelValue.enabled
          ? t('LIA_AGENTS.TEST_MODE.ACTIVATED', {
              count: props.modelValue.phone_numbers.length,
            })
          : t('LIA_AGENTS.TEST_MODE.DEACTIVATED');
        useAlert(statusMsg);
      } catch (deployError) {
        const errorMsg =
          deployError.response?.data?.error ||
          deployError.message ||
          'Erro desconhecido';
        useAlert(t('LIA_AGENTS.TEST_MODE.REDEPLOY_TITLE') + ': ' + errorMsg);
      }
    } else {
      useAlert(t('LIA_AGENTS.TEST_MODE.SAVED_NEEDS_DEPLOY'));
    }
  } catch (error) {
    useAlert(error.message || t('LIA_AGENTS.EDIT.SAVE_ERROR'));
  } finally {
    isSavingTestMode.value = false;
  }
};

// Format phone number for display
const formatPhoneDisplay = phone => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 13) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return phone;
};
</script>

<template>
  <div class="space-y-6">
    <!-- Test Mode Toggle Card -->
    <div class="bg-n-solid-3 rounded-xl p-6">
      <div class="flex items-start gap-4">
        <div
          class="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          :class="[
            testModeConfig.enabled
              ? 'bg-yellow-100 dark:bg-yellow-900/30'
              : 'bg-n-alpha-2',
          ]"
        >
          <span
            class="i-lucide-flask-conical size-6"
            :class="[
              testModeConfig.enabled
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-n-slate-11',
            ]"
          />
        </div>
        <div class="flex-1">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ t('LIA_AGENTS.TEST_MODE.TITLE') }}
            </h3>
            <!-- Toggle Switch -->
            <button
              type="button"
              class="relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              :class="[
                testModeConfig.enabled
                  ? 'bg-yellow-500'
                  : 'bg-n-slate-9 dark:bg-n-slate-7',
              ]"
              @click="toggleEnabled"
            >
              <span
                class="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200"
                :class="testModeConfig.enabled ? 'translate-x-7' : ''"
              />
            </button>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ t('LIA_AGENTS.TEST_MODE.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Status Badge -->
      <div
        class="mt-4 p-3 rounded-lg"
        :class="[
          testModeConfig.enabled
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
            : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800',
        ]"
      >
        <div class="flex items-center gap-2">
          <span
            :class="[
              testModeConfig.enabled
                ? 'i-lucide-flask-conical text-yellow-600 dark:text-yellow-400'
                : 'i-lucide-globe text-green-600 dark:text-green-400',
            ]"
            class="size-5"
          />
          <span
            class="text-sm font-medium"
            :class="[
              testModeConfig.enabled
                ? 'text-yellow-700 dark:text-yellow-300'
                : 'text-green-700 dark:text-green-300',
            ]"
          >
            {{
              testModeConfig.enabled
                ? t('LIA_AGENTS.TEST_MODE.STATUS_ACTIVE')
                : t('LIA_AGENTS.TEST_MODE.STATUS_PRODUCTION')
            }}
          </span>
        </div>
      </div>
    </div>

    <!-- Phone Numbers Card -->
    <div
      class="bg-n-solid-3 rounded-xl p-6"
      :class="{ 'opacity-50': !testModeConfig.enabled }"
    >
      <h3
        class="text-lg font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
      >
        <span class="i-lucide-smartphone size-5 text-woot-500" />
        {{ t('LIA_AGENTS.TEST_MODE.PHONE_NUMBERS_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ t('LIA_AGENTS.TEST_MODE.PHONE_NUMBERS_SUBTITLE') }}
      </p>

      <!-- Add Phone Number -->
      <div class="mb-4">
        <div class="flex items-center gap-2">
          <input
            v-model="newPhoneNumber"
            type="tel"
            :placeholder="t('LIA_AGENTS.TEST_MODE.PHONE_PLACEHOLDER')"
            :disabled="!testModeConfig.enabled"
            class="flex-1 px-4 py-2.5 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
            @keyup.enter="addTestPhoneNumber"
          />
          <button
            type="button"
            class="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors disabled:opacity-50"
            :disabled="!testModeConfig.enabled || !newPhoneNumber.trim()"
            @click="addTestPhoneNumber"
          >
            <span class="i-lucide-plus size-4" />
            {{ t('LIA_AGENTS.TEST_MODE.ADD_BUTTON') }}
          </button>
        </div>
        <p class="mt-2 text-xs text-n-slate-10">
          {{ t('LIA_AGENTS.TEST_MODE.PHONE_FORMAT_HELP') }}
        </p>
      </div>

      <!-- Phone Numbers List -->
      <div v-if="testModeConfig.phone_numbers.length > 0" class="space-y-2">
        <div
          v-for="phone in testModeConfig.phone_numbers"
          :key="phone"
          class="flex items-center justify-between p-3 bg-n-alpha-2 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-smartphone size-4 text-n-slate-11" />
            <span class="text-sm font-mono text-n-slate-12">{{
              formatPhoneDisplay(phone)
            }}</span>
          </div>
          <button
            type="button"
            class="p-1.5 text-n-slate-11 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            :disabled="!testModeConfig.enabled"
            @click="removeTestPhoneNumber(phone)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-8 border-2 border-dashed border-n-weak rounded-lg"
      >
        <span class="i-lucide-phone-off size-10 text-n-slate-10 mx-auto mb-3" />
        <p class="text-sm text-n-slate-11">
          {{ t('LIA_AGENTS.TEST_MODE.EMPTY_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ t('LIA_AGENTS.TEST_MODE.EMPTY_SUBTITLE') }}
        </p>
      </div>

      <!-- Count -->
      <p
        v-if="testModeConfig.phone_numbers.length > 0"
        class="mt-4 text-xs text-n-slate-11"
      >
        {{
          t('LIA_AGENTS.TEST_MODE.COUNT', {
            count: testModeConfig.phone_numbers.length,
          })
        }}
      </p>
    </div>

    <!-- Save Button -->
    <button
      type="button"
      class="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors disabled:opacity-50"
      :disabled="isSavingTestMode"
      @click="saveTestModeSettings"
    >
      <span
        v-if="isSavingTestMode"
        class="i-lucide-loader-2 size-4 animate-spin"
      />
      <span v-else class="i-lucide-save size-4" />
      {{
        isSavingTestMode
          ? t('LIA_AGENTS.TEST_MODE.SAVING')
          : t('LIA_AGENTS.TEST_MODE.SAVE_BUTTON')
      }}
    </button>

    <!-- Redeploy Warning -->
    <div
      v-if="agent?.is_active"
      class="flex items-start gap-3 p-4 bg-woot-50 dark:bg-woot-900/20 border border-woot-200 dark:border-woot-800 rounded-xl"
    >
      <span
        class="i-lucide-info size-5 text-woot-600 dark:text-woot-400 flex-shrink-0 mt-0.5"
      />
      <div>
        <p class="text-sm font-medium text-woot-800 dark:text-woot-300">
          {{ t('LIA_AGENTS.TEST_MODE.REDEPLOY_TITLE') }}
        </p>
        <p class="text-xs text-woot-700 dark:text-woot-400 mt-1">
          {{ t('LIA_AGENTS.TEST_MODE.REDEPLOY_INFO') }}
        </p>
      </div>
    </div>

    <!-- Help Card -->
    <div class="bg-n-alpha-2 rounded-xl p-6 border border-n-weak">
      <h4
        class="text-sm font-semibold text-n-slate-12 mb-3 flex items-center gap-2"
      >
        <span class="i-lucide-help-circle size-4 text-yellow-500" />
        {{ t('LIA_AGENTS.TEST_MODE.HELP_TITLE') }}
      </h4>
      <div class="space-y-3 text-sm text-n-slate-11">
        <div class="flex items-start gap-2">
          <span
            class="i-lucide-check size-4 text-green-500 mt-0.5 flex-shrink-0"
          />
          <span>
            <strong class="text-n-slate-12">
              {{ t('LIA_AGENTS.TEST_MODE.HELP_ACTIVE') }}
            </strong>
            {{ t('LIA_AGENTS.TEST_MODE.HELP_ACTIVE_DESC') }}
          </span>
        </div>
        <div class="flex items-start gap-2">
          <span
            class="i-lucide-check size-4 text-green-500 mt-0.5 flex-shrink-0"
          />
          <span>
            <strong class="text-n-slate-12">
              {{ t('LIA_AGENTS.TEST_MODE.HELP_INACTIVE') }}
            </strong>
            {{ t('LIA_AGENTS.TEST_MODE.HELP_INACTIVE_DESC') }}
          </span>
        </div>
        <div class="flex items-start gap-2">
          <span
            class="i-lucide-check size-4 text-green-500 mt-0.5 flex-shrink-0"
          />
          <span>
            <strong class="text-n-slate-12">
              {{ t('LIA_AGENTS.TEST_MODE.HELP_TIP_LABEL') }}
            </strong>
            {{ t('LIA_AGENTS.TEST_MODE.HELP_TIP') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
